import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Dropdown, DropdownMenuItem, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import CaseInboxItem from './CaseInboxItem';

const CaseInbox = (props) => {
  const {
    casesLoadFailed,
    caseDateRanges,
    caseTypeGroupKey,
    caseTypeGroups,
    dateRangeKey,
    isLoading,
    lastEvaluatedKey,
    loadMoreCases,
    pendingOnly,
    selectedCaseRn,
    setCaseTypeGroupKey,
    setDateRangeKey,
    setPendingOnly,
    selectCaseRn,
    visibleCases,
  } = props;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        width: {
          xs: '100%',
          md: '324px',
          lg: '324px',
        },
        borderRight: {
          md: '1px solid #ECECF1',
        },
        minHeight: {
          xs: '360px',
          md: 0,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          p: '22px 18px 14px 20px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              color: '#050A14',
            }}
          >
            <Translate expression='case.List.Title' />
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              color: '#9A9BAE',
            }}
          >
            {translate('case.List.CaseCount', {count: visibleCases.length})}
          </Typography>
        </Box>
        <Box
          sx={{
            'width': '100%',
            '& .MuiInputLabel-root, & .MuiInputLabel-shrink': {
              position: 'relative',
              transform: 'none',
              mb: '6px',
              fontSize: '11px',
              lineHeight: '14px',
              color: '#9A9BAE',
            },
            '& .MuiOutlinedInput-root': {
              'height': '37px',
              'borderRadius': '9px',
              'backgroundColor': '#FFFFFF',
              'fontSize': '13px',
              'color': '#050A14',
              '& fieldset, &.Mui-focused fieldset': {
                borderColor: '#DCDDE5',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: '#D1D3DD',
              },
            },
            '& .MuiSelect-select': {
              py: '8px',
              px: '12px',
            },
            '& legend': {
              display: 'none',
            },
          }}
        >
          <Dropdown
            disabled={isLoading}
            label={translate('case.List.DateRange.Label')}
            onChange={setDateRangeKey}
            value={dateRangeKey}
          >
            {
              caseDateRanges.map((dateRange) => (
                <DropdownMenuItem
                  key={dateRange.key}
                  value={dateRange.key}
                >
                  {translate(`case.List.DateRange.Options.${dateRange.key}`)}
                </DropdownMenuItem>
              ))
            }
          </Dropdown>
        </Box>
        <Box
          sx={{
            'width': '100%',
            'mt': 1,
            '& .MuiInputLabel-root, & .MuiInputLabel-shrink': {
              position: 'relative',
              transform: 'none',
              mb: '6px',
              fontSize: '11px',
              lineHeight: '14px',
              color: '#9A9BAE',
            },
            '& .MuiOutlinedInput-root': {
              'height': '37px',
              'borderRadius': '9px',
              'backgroundColor': '#FFFFFF',
              'fontSize': '13px',
              'color': '#050A14',
              '& fieldset, &.Mui-focused fieldset': {
                borderColor: '#DCDDE5',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: '#D1D3DD',
              },
            },
            '& .MuiSelect-select': {
              py: '8px',
              px: '12px',
            },
            '& legend': {
              display: 'none',
            },
          }}
        >
          <Dropdown
            disabled={isLoading}
            label={translate('case.List.CaseTypeGroup.Label')}
            onChange={setCaseTypeGroupKey}
            value={caseTypeGroupKey}
          >
            {
              caseTypeGroups.map((caseTypeGroup) => (
                <DropdownMenuItem
                  key={caseTypeGroup.key}
                  value={caseTypeGroup.key}
                >
                  {translate(`case.List.CaseTypeGroup.Options.${caseTypeGroup.key}`)}
                </DropdownMenuItem>
              ))
            }
          </Dropdown>
        </Box>
        <Button
          aria-pressed={pendingOnly}
          disabled={isLoading}
          onClick={() => setPendingOnly((currentPendingOnly) => !currentPendingOnly)}
          sx={{
            'alignSelf': 'flex-start',
            'height': '30px',
            'px': '12px',
            'py': '6px',
            'border': '1px solid',
            'borderColor': pendingOnly ? '#EB7A58' : '#DCDDE5',
            'borderRadius': '999px',
            'backgroundColor': pendingOnly ? '#FFF4EE' : '#FFFFFF',
            'boxShadow': 'none',
            'color': pendingOnly ? '#C4512C' : '#526071',
            'fontSize': '12px',
            'fontWeight': 700,
            'lineHeight': '16px',
            'textTransform': 'none',
            '&:hover': {
              'borderColor': pendingOnly ? '#D96845' : '#D1D3DD',
              'backgroundColor': pendingOnly ? '#FFF1EA' : '#F7F8FA',
              'boxShadow': 'none',
            },
          }}
          variant='outlined'
        >
          {translate('case.List.PendingOnly')}
        </Button>
      </Box>
      <Stack
        sx={{
          'gap': 1,
          'flex': 1,
          'minHeight': 0,
          'overflowY': 'auto',
          'px': 2,
          'pb': 1.5,
          'pt': 0.75,
          'scrollbarColor': '#CFCFD4 transparent',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#CFCFD4',
            borderRadius: '999px',
          },
        }}
      >
        {
          visibleCases.map((caseData) => (
            <CaseInboxItem
              key={caseData.caseRn}
              caseData={caseData}
              isSelected={caseData.caseRn === selectedCaseRn}
              onSelect={selectCaseRn}
            />
          ))
        }
        {
          isLoading && visibleCases.length === 0 && (
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#6D7280',
                px: 1,
                py: 2,
              }}
            >
              <Translate expression='case.List.Loading' />
            </Typography>
          )
        }
        {
          casesLoadFailed && (
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#D85D31',
                px: 1,
                py: 2,
              }}
            >
              <Translate expression='case.List.LoadFailed' />
            </Typography>
          )
        }
        {
          !isLoading && !casesLoadFailed && visibleCases.length === 0 && (
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#6D7280',
                px: 1,
                py: 2,
              }}
            >
              <Translate expression='case.List.Empty' />
            </Typography>
          )
        }
      </Stack>
      {
        lastEvaluatedKey && (
          <Box
            sx={{
              p: 1.5,
              borderTop: '1px solid #ECECF1',
            }}
          >
            <Button
              disabled={isLoading}
              fullWidth
              onClick={loadMoreCases}
              sx={{
                'height': '32px',
                'px': '10px',
                'py': '8px',
                'border': '1px solid #DCDDE5',
                'borderRadius': '7px',
                'backgroundColor': '#FFFFFF',
                'color': '#6D7280',
                'textTransform': 'none',
                'boxShadow': 'none',
                '&:hover': {
                  'border': '1px solid #D1D3DD',
                  'backgroundColor': '#F7F8FA',
                  'boxShadow': 'none',
                },
              }}
              variant='outlined'
            >
              {translate('case.List.LoadMore')}
            </Button>
          </Box>
        )
      }
    </Box>
  );
};

CaseInbox.propTypes = {
  casesLoadFailed: PropTypes.bool.isRequired,
  caseDateRanges: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
  })).isRequired,
  caseTypeGroupKey: PropTypes.string.isRequired,
  caseTypeGroups: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
  })).isRequired,
  dateRangeKey: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  lastEvaluatedKey: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  loadMoreCases: PropTypes.func.isRequired,
  pendingOnly: PropTypes.bool.isRequired,
  selectedCaseRn: PropTypes.string,
  setCaseTypeGroupKey: PropTypes.func.isRequired,
  setDateRangeKey: PropTypes.func.isRequired,
  setPendingOnly: PropTypes.func.isRequired,
  selectCaseRn: PropTypes.func.isRequired,
  visibleCases: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default React.memo(CaseInbox);
